import { createApp, onMounted, h } from "vue";
import AppLayout from "@Layouts/AppLayout";
import GuestLayout from "@Layouts/GuestLayout";
import NProgress from "nprogress";
import VueTippy from "vue-tippy";
import { patchHeadlessuiPortalRoot } from "./patches";
import App from "./App.vue";
import router from "./router";
import notifications from "./notifications";
import pricingPlans from "./pricingPlans";
import axios from "./axios";
import form from "./form";
import "./theme";
import { i18n } from "./i18n";
import { createPinia } from "pinia";
import { useStore as useDomainStore } from "@/Stores/domain";
import { useStore as useUserStore } from "@/Stores/user";
import { useStore as useRequiredSettingsStore } from "@/Stores/requiredSettings";
import { useStore as usePricingPlansStore } from "@/Stores/pricingPlans";
import { useStore as useTemplatesStore } from "@/Stores/templates";
import { useStore as useTextBlocksStore } from "@/Stores/textBlocks";

import "@fontsource/inter";
import "../css/app.css";

const el = document.getElementById("otomatic-ai-app");

NProgress.configure({
    parent: "#nprogress-container",
    showSpinner: false,
});

const pinia = createPinia();

export const app = createApp({
    setup() {
        patchHeadlessuiPortalRoot("otomatic-ai-app");

        const domainStore = useDomainStore();
        const userStore = useUserStore();
        const requiredSettingsStore = useRequiredSettingsStore();
        const pricingPlansStore = usePricingPlansStore();
        const templatesStore = useTemplatesStore();
        const textBlocksStore = useTextBlocksStore();

        domainStore.setDomain(app.config.globalProperties.$app.domain);
        userStore.setUser(app.config.globalProperties.$app.user);
        onMounted(() => {
            requiredSettingsStore.fetchRequiredSettings();
            pricingPlansStore.fetch();
            templatesStore.fetch();
            textBlocksStore.fetch();
        });
    },

    render(a, b, c) {
        return h(App);
    },
});

// app.config.unwrapInjectedRef = true;
app.config.globalProperties.$app = window.otomatic_ai;

app.config.globalProperties.$asset = (path) => {
    if (path === undefined) {
        path = "";
    }
    return app.config.globalProperties.$app.public_path.replace(/\/$/, "") + "/" + path.replace(/^\//, "");
};
app.config.globalProperties.$admin = (path) => {
    if (path === undefined) {
        path = "";
    }
    return app.config.globalProperties.$app.admin_url.replace(/\/$/, "") + "/" + path.replace(/^\//, "");
};
app.provide("app", app.config.globalProperties.$app);
app.provide("admin", app.config.globalProperties.$admin);

app.component("AppLayout", AppLayout);
app.component("GuestLayout", GuestLayout);

app.use(pinia);
app.use(router);
app.use(notifications);
app.use(pricingPlans);
app.use(i18n);
app.use(axios, {
    onError: (err) => {
        const data = err.response.data;
        if (data.message !== undefined || data.error !== undefined) {
            app.config.globalProperties.$notify({
                title: data.message,
                body: data.error,
                type: "error",
            });
        } else {
            app.config.globalProperties.$notify({
                title: err.code,
                body: err.message,
                type: "error",
            });
        }
    },
});
app.use(form, {
    ajaxurl: window.otomatic_ai?.ajaxurl,
    nonce: window.otomatic_ai?.nonce,
    prefix: "otomatic_ai_",
    onError: (err) => {
        const data = err.response.data;
        if (data.message !== undefined || data.error !== undefined) {
            app.config.globalProperties.$notify({
                title: data.message,
                body: data.error,
                type: "error",
            });
        } else {
            app.config.globalProperties.$notify({
                title: err.code,
                body: err.message,
                type: "error",
            });
        }
    },
});
app.use(VueTippy, {
    defaultProps: {
        appendTo: el,
        delay: 100,
        animation: "fade",
        zIndex: 100010,
        allowHTML: true,
    },
    directive: "tippy",
});
app.mount(el);

export default app;
